/**
 * Get the scrollable DOM node from a React Native Web ScrollView ref
 */
export const getScrollableNodeFromRef = (scrollContainerRef) => {
  const refCurrent = scrollContainerRef?.current;
  if (!refCurrent) {
    return null;
  }

  // React Native Web ScrollView exposes getScrollableNode() which returns the actual DOM element.
  const scrollableNode = refCurrent.getScrollableNode?.();
  if (scrollableNode) {
    return scrollableNode;
  }

  // If ref already points to a DOM element, it should have scrollTop.
  if (refCurrent.scrollTop !== undefined) {
    return refCurrent;
  }

  // Fallback for cases where the ref points to a wrapper element.
  const firstChild = refCurrent.children?.[0];
  if (firstChild && firstChild.scrollTop !== undefined) {
    return firstChild;
  }

  return null;
};

/**
 * Check if a reference element is usable for FloatingUI positioning
 */
export const isUsableReferenceElement = (referenceElement) => {
  if (!referenceElement) {
    return false;
  }

  // On web, refs usually point to DOM elements.
  const isDomElement =
    typeof Element !== "undefined" && referenceElement instanceof Element;

  if (isDomElement && referenceElement.isConnected === false) {
    return false;
  }

  if (typeof window !== "undefined") {
    const getRect = referenceElement.getBoundingClientRect;
    if (typeof getRect === "function") {
      const rect = getRect.call(referenceElement);
      // If the element is effectively not laid out (common when view is hidden),
      // FloatingUI can fall back to (0,0). Treat this as unusable.
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Check if an event target is an editable element (input, textarea, etc.)
 */
export const isEditableEventTarget = (target) => {
  if (!target || typeof window === "undefined") {
    return false;
  }

  const el = target;

  // If this is a text-editable element, do not hijack arrow/enter keys.
  const tagName = (el.tagName || "").toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }

  if (el.isContentEditable) {
    return true;
  }

  return false;
};
